import React, { useState, useEffect } from 'react';
import { useFlowBuilderStore } from '../../../stores/flowBuilderStore';
import { api } from '../../../lib/api';
import { Loader2 } from 'lucide-react';

interface TransferToOperatorConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

export const TransferToOperatorConfig: React.FC<TransferToOperatorConfigProps> = ({ config, onChange }) => {
  const { connectionId } = useFlowBuilderStore();
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadUsers = async () => {
      if (!connectionId) return;
      setIsLoading(true);
      try {
        const users = await api.get<any[]>(`/bitrix/connection/${connectionId}/users`);
        if (Array.isArray(users)) {
            setUsers(users);
        } else if (users && typeof users === 'object') {
            setUsers(Object.values(users));
        } else {
            setUsers([]);
        }
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [connectionId]);

  const operatorId = config.operatorId || '';
  const selectedFromList = users.find((u: any) => String(u.ID) === String(operatorId));
  const isManualId = operatorId && !selectedFromList;

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 leading-relaxed">
        <strong>Como funciona:</strong> Transfere a conversa para um operador humano. Você pode escolher um operador específico ou deixar em branco para que a conversa fique disponível para qualquer atendente. O bot para de responder após a transferência.
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Selecione o Operador (Opcional)
        </label>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando operadores...
          </div>
        ) : (
          <select
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedFromList ? operatorId : ''}
            onChange={(e) => onChange('operatorId', e.target.value)}
          >
            <option value="">Qualquer operador disponível</option>
            {users.map((user: any) => (
              <option key={user.ID} value={user.ID}>
                {user.NAME} {user.LAST_NAME} (ID: {user.ID})
              </option>
            ))}
          </select>
        )}
        <p className="text-xs text-slate-500 mt-1">
          Se nenhum operador for selecionado, o chat será enviado para a fila geral.
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Ou informe o ID do operador diretamente
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Ex: 42 ou {{contact_id}}"
          value={operatorId}
          onChange={(e) => onChange('operatorId', e.target.value)}
        />
        <p className="text-xs text-slate-500 mt-1">
          Aceita ID numérico ou variável do fluxo (ex: <code className="bg-slate-100 px-1 rounded">{'{{contact_id}}'}</code>).
          {isManualId && (
            <span className="ml-1 text-amber-600">ID manual ativo: <strong>{operatorId}</strong></span>
          )}
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Mensagem de Transferência
        </label>
        <textarea
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Transferindo para um atendente..."
          value={config.message || ''}
          onChange={(e) => onChange('message', e.target.value)}
        />
      </div>
    </div>
  );
};
